import { ChangeDetectorRef, Component, ErrorHandler, Injector, OnDestroy, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy, WritableSignal, inject, signal, untracked } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { applyEach, disabled, FieldTree, form, metadata, ReadonlyFieldTree, REQUIRED, required, validate, validateTree } from '@angular/forms/signals'
import { toSignal } from '@angular/core/rxjs-interop'
import { MatSnackBar } from '@angular/material/snack-bar'

import { BankConnection, BankConnectionDisableRequest, BankConnectionDisableResponse } from '@lemon/core-firebase/model/bank-connection'
import {
  MAKSUTAPA_MUU,
  MAKSUTAPA_MYYNTILASKU,
  MAKSUTAPA_MYYNTITOSITE,
  MAKSUTAPA_PALKKATOSITE,
  MAKSUTAPA_SAHKOISET_LASKUT,
  MAKSUTAPA_SAHKOISET_LASKUT_FAKE,
  MAKSUTAPA_TILIOTETOSITE,
} from '@lemon/core-firebase/model/kayttaja'
import { SahkoisenTiliotteenAlatyyppi, Tilitapahtuma } from '@lemon/core-firebase/model/tiliote'
import { Asiakas, AsiakasKytkeMaksuOminaisuusRequest, AsiakasKytkeMaksuOminaisuusResponse, AsiakkaanMaksutapa, Kayttaja, LemonatorPaymentConfig } from '../_jaettu-lemonator/model/asiakas'
import { KirjanpitajanNimitiedot } from '../_jaettu-lemonator/model/kirjanpitaja'
import { Kirjanpitotili, KirjauksienLuontityyppi, PoistaTilitapahtumatJaKirjauksetPyynto, PoistaTilitapahtumatJaKirjauksetVastaus } from '../_jaettu-lemonator/model/kirjanpito'

import { BicService } from '@lemon/core/service/bic.service'
import { DateService } from '@lemon/core/service/date.service'
import { IbanService } from '@lemon/core/service/iban.service'
import { StringService } from '@lemon/core/service/string.service'
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'
import { AsiakasService } from '../_angular/service/asiakas/asiakas.service'
import { TilikarttaService } from '../_angular/service/tilikartta.service'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'

import { MatDialog } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle'
import { AjastettuTyo, AjastettuTyoService } from '@lemon/core-firebase/eraajot/ajastettu/ajastetut-tyot.service'
import { ApixReceivedInvoiceConfig } from '@lemon/core-firebase/model/apix'
import { LocalDateTime } from '@lemon/core/model/common'
import { getPaymentSupportedBanks, PaymentSupportedBank, validBanksForBic } from '@lemon/core/model/payment'
import { AsiakasKopioija } from 'app/_angular/service/asiakas/asiakas.kopioija'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { TilikarttaJaettuService } from 'app/_jaettu-lemonator/service/tilikartta-jaettu.service'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of as observableOf, of, Subject } from 'rxjs'
import { debounceTime, map, startWith, switchMap, take, takeUntil, takeWhile, tap, withLatestFrom } from 'rxjs/operators'
import { KirjanpitajaService } from '../_angular/service/kirjanpitaja/kirjanpitaja.service'
import { AreYouSureDialog, AreYouSureDialogData } from '../_jaettu-angular/_components/are-you-sure.dialog'
import { AktivoiMaksutavanPalveluntarjoajanVaihtoWorkQueue, PalveluntarjoajanVaihdonYhteystarkistusTyojono } from '../_jaettu-lemonator/model/asiakas'
import { KirjanpitoJaettuService } from '../_jaettu-lemonator/service/kirjanpito-jaettu.service'
import {
  AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialog,
  AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialogData,
  AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialogResult,
} from './asiakkaan-maksutavat-aloita-provider-vaihto'
import {
  AsiakkaanMaksutavatPoistaTilitapahtumatDialog,
  AsiakkaanMaksutavatPoistaTilitapahtumatDialogData,
  AsiakkaanMaksutavatPoistaTilitapahtumatDialogResult,
} from './asiakkaan-maksutavat-poista-tilitapahtumat.dialog'
import { AsiakkaanMaksutavatHistoriaDialog } from './asiakkaan-maksutavat.historia.dialog'
import { AsiakkaanMaksutavatValitseKuvakeDialog } from './asiakkaan-maksutavat.valitse-kuvake.dialog'

interface LokalisoituTiliotetyyppi {
  nimi: string
  tunniste: KirjauksienLuontityyppi
  alitunniste: SahkoisenTiliotteenAlatyyppi
  vainLuottokirjanpitajalle?: boolean
  poistunutKaytosta?: boolean
}

interface MaksutapaJaKayttajat {
  kayttajat: Kayttaja[]
  maksutapa: AsiakkaanMaksutapa
  uusi: boolean
  onkoValtuutus: boolean
}

interface PankkiJaIban extends Pick<BankConnection, 'iban' | 'disabledInLemonator' | 'source'> {
  pankki: string
  bic: string
}

interface MaksutapaLomake {
  nimi: string
  kirjaustenLuontityyppi: LokalisoituTiliotetyyppi | null
  sahkoisenTiliotteenTunniste: string | null
  oletusVastatili: string | null
  aktiivinen: boolean | null
  tilitapahtumatSynkronoidaanAlkaen: Date | null
  tilitapahtumatSynkronoidaanLoppuen: Date | null
  omallaRahallaUid: AsiakkaanMaksutapa['omallaRahallaUid'] | null
}

interface MaksutavatLomake {
  maksutavat: MaksutapaLomake[]
}

interface TilinumeronValidointi {
  iban: boolean
  pakollinen: boolean
  /** undefined: pankin tarkistusta ei tehdä */
  bicAlatyyppi: SahkoisenTiliotteenAlatyyppi | null | undefined
}

interface MaksutapaRivinTila {
  luontityyppiPoissa: WritableSignal<boolean>
  tilinumeroPoissa: WritableSignal<boolean>
  tilinumeronValidointi: WritableSignal<TilinumeronValidointi>
  alkaenPoissa: WritableSignal<boolean>
  loppuenPoissa: WritableSignal<boolean>
  vastatiliPoissa: WritableSignal<boolean>
  lopetus: Subject<void>
}

interface MaksuConfigLomake {
  start: Date | null
  end: Date | null
  iban: string | null
  bank: PaymentSupportedBank | null
  debtorIdentifier: string
}

interface MaksuConfigsLomake {
  hyvaksyjienLukumaara: number | null
  configs: MaksuConfigLomake[]
}

@Component({
  templateUrl: './asiakkaan-maksutavat.component.html',
  styleUrls: ['./asiakkaan-maksutavat.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanMaksutavatComponent implements OnInit, OnDestroy {
  @ViewChildren('debtorIdentifier', { read: MatInput }) debtorIdentifierit: QueryList<MatInput>

  // @ViewChild(MatInput) etunimiInput: MatInput
  // @ViewChild('fileInput', { static: true }) fileInput: ElementRef

  // eslint-disable-next-line max-len
  private _defaultMaksutapaKuva =
    'iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAHlBMVEX09PTh4eH19fXg4ODk5OTw8PDs7Ozq6uru7u7n5+dZKxXMAAAELUlEQVR4nO2dWXKtMAwFwQMX9r/hB9RNMOARhCTyTv/kIxVQl4kHYZmuAwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADgAsZ03o9eA6OfwyH360bXWz30kyd2HHvbq8LaibQdJ2V+C7Yf6RSdQsEZOxApGqWCsyJNK5qPVsEZCsHOS1tksBNFI2rsZTY8gaG0QxaCRjRj0ITS4/wvW0judguaKbjaID1f+xLEZG8/psZtV6OeKF3GbH2DHW9fbDN0WgTn/j0wvBtVYEjSM9MAwxZgKAMMW4ChDLyGxoQ/eGA0NMYPk3O9c9PH80nyGZo1A/e9l3WEiZM8DxmeZm3GH/Ib1jFNXZna0AzntTFZcigPj2E8f2M/HIoshqkEFYsih+Fu7b9XZOhvWNow4bdw85YVMBjmkqg0Kb4sHG2YaUKaFF+W5w3zefDnO5vnR/wwPxXh8YwOw1Oaz4Pbmzct8ryhLxhe+EdsivRxw/RgePW2821a/uZ5w8ILt/auZp7Ct8Qqb9g6/17XKA3BKnhK227yswirjvZtPc22yqwN912jxW6bQGW87xrx93mCuoBfNWs7JkKqIuaYeWcf04brn3ey1ITMsXrK7F9oWD1Ft+pUxPyeFXB8L1I5aJYsRnKbTcM9E5utyleQzUTVz2eSu8mKYb8km5jbLleI+xUZ4cJ+wHzgr8jqFzY85iNnfPe0vJn5uVff8mamuKMzGzrn27Xu9+3a4Bu2JddsWc3Erv4NaeWe3HTw6t9yV246Tkev3bB6V3UyfOWGDdvGU/HrNmzbFx8X0GzYvPE/asA14l+hubIhqqC4DS+UbsQc9Bpeq005S2g1vFx8c7LQani5uuikIWhohnR5wI3yqaOHnOG8ZLQpxVv1YYd1p5jhuiZOKN4sgNsrShl+F/0xxfsVfjtFoRE/yGqcfkdQwhgqyrRhmLY5tiJJjWbwvkfEcJeXOjyoNEWoVrYND4m3UJGqyla2DU+ZxUCRqoxY1DCWOv3pbsjqpCUNo4LfVqQrBBc0jAquioaylF/OMCG4KlKW8j9vmBjxk4LUSLUhm6CUIZ+gkCGjoIwhp6CIIaughCGvoIAhsyC/Ibcg+4jPLsjdhvyCzIYCgryGEoK8hoV93u83lDn+i9VQ4iGFIQwrDN2fN/z7bQhDGMIQhjCEYdnwvxrxPyKnljIadn6QgNOwMxKwGgoDwxZgKAMMW4ChDE/N2kiCoyA4eYTgPO/t/PPluyc6COrICc5kDybZ1jodhPP+u4LFg1qkofjXyR9jIgzFaaKl84Rkoen9FDci1Tmb0h5JqM6gNFo7m2R5zgVFbd8kW5nHZzJOx0MowNqhHHiL4+h2H7ARZvl4HvUHAtfD1yfpycyX6TPSfwCxE8o+JXhADwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAs/ANyGFT0fw3sTAAAAABJRU5ErkJggg=='

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _lisaaKuvaIndeksiin: number = 0
  private _sallitutKirjanpitotilinumerot: Set<string>
  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _kaikkiKirjanpitotilitObservable: Observable<Kirjanpitotili[]>

  maksutavatUriObservable: Observable<string>
  maksutavatEncodedUriObservable: Observable<string>
  maksutavatLemonaidCustomersUriObservable: Observable<string>
  maksutavatLemonaidCustomersEncodedUriObservable: Observable<string>
  maksutavatLemonaidRealtimeDbUriObservable: Observable<{ kayttaja: string; uri: string; encodedUri: string }[]>
  bankConnectionsUriObservable: Observable<string>
  bankConnectionsEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean> = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
  kirjanpitajaOnDevaajaTaiTukiObservable: Observable<boolean> = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiObservable
  kirjanpitajaOnLuottoObservable: Observable<boolean> = this._kirjautunutKayttajaService.kirjanpitajaOnLuottoObservable
  naytaMaksutilitObservable: Observable<boolean>
  asiakkaanApixTiedotObservable: Observable<Partial<ApixReceivedInvoiceConfig>>
  asiakkaanOstolaskujenMaksajaRoolinKayttajatObservable: Observable<Kayttaja[]>
  asiakkaanOstolaskujenHyvaksyjaRoolinKayttajatObservable: Observable<Kayttaja[]>
  readonly kirjaustenLuontityypit = this._annaKirjaustenLuontityypit()
  private readonly _kirjaustenLuontityypitByTunnisteetMap = new Map(this.kirjaustenLuontityypit.map(tyyppi => [`${tyyppi.tunniste}_${tyyppi.alitunniste}`, tyyppi]))
  asiakkaanIbanitJaPankitObservable: Observable<PankkiJaIban[]>
  readonly maksamisenPankit = getPaymentSupportedBanks()
  kirjanpitajienNimitiedotMapObservable: Observable<Map<string, KirjanpitajanNimitiedot>> = this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable
  private _kirjanpitajaOnDevaajaTaiTuki: boolean = false
  readonly kirjanpitajaOnLuotto = toSignal(this._kirjautunutKayttajaService.kirjanpitajaOnLuottoObservable, { initialValue: false })

  maksatustunnusEhdotus: string

  suodatetutKirjanpitotilitSubject: BehaviorSubject<Kirjanpitotili[]> = new BehaviorSubject([])
  suodatetutKirjanpitotilitObservable: Observable<Kirjanpitotili[]> = this.suodatetutKirjanpitotilitSubject.asObservable()

  private _maksutavatChangedSubject: Subject<void> = new Subject()
  isHolviUlkopTositteetVisible: boolean = false
  isOmallaRahallaColumnVisible: boolean = false
  isHolviApiSelectionDisabled: boolean = false

  eiTilikausia: boolean = false
  readonly commonError = signal<string>(undefined)
  commonErrorPaymentConfigs: string
  saveStarted: boolean
  readonly lataaIbaneja = signal(true)
  lataaMaksutapoja: boolean = true
  private readonly _injector = inject(Injector)
  private readonly _rivienTilat = new WeakMap<object, MaksutapaRivinTila>()
  private readonly _vastatilienVersio = signal(0)
  private readonly _hyvaksyjienLukumaaraPakollinen = signal(true)
  private _maksuConfigienLopetukset: Subject<void>[] = []

  lomake: FieldTree<MaksutavatLomake> = form(signal<MaksutavatLomake>({ maksutavat: [] }), polku => {
    applyEach(polku.maksutavat, rivi => {
      required(rivi.nimi)
      disabled(rivi.kirjaustenLuontityyppi, ({ fieldTreeOf }) => this._rivinTila(fieldTreeOf(rivi)).luontityyppiPoissa())
      required(rivi.kirjaustenLuontityyppi)
      disabled(rivi.sahkoisenTiliotteenTunniste, ({ fieldTreeOf }) => this._rivinTila(fieldTreeOf(rivi)).tilinumeroPoissa())
      // Tähti näytetään aina, vaikka pakollisuus vaihtelee kirjausten luontityypin mukaan
      metadata(rivi.sahkoisenTiliotteenTunniste, REQUIRED, () => true)
      required(rivi.sahkoisenTiliotteenTunniste, { when: ({ fieldTreeOf }) => this._rivinTila(fieldTreeOf(rivi)).tilinumeronValidointi().pakollinen })
      validoiReaktiivisesti(rivi.sahkoisenTiliotteenTunniste, ctrl => this._validateIban(ctrl), { when: ({ fieldTreeOf }) => this._rivinTila(fieldTreeOf(rivi)).tilinumeronValidointi().iban })
      validate(rivi.sahkoisenTiliotteenTunniste, ({ value, fieldTreeOf }) => {
        const bicAlatyyppi = this._rivinTila(fieldTreeOf(rivi)).tilinumeronValidointi().bicAlatyyppi
        if (bicAlatyyppi === undefined) {
          return null
        }
        return this._alatyyppiAllowedBicsValidator(bicAlatyyppi)({ value: value() } as AbstractControl) ? { kind: 'invalidbic' } : null
      })
      disabled(rivi.oletusVastatili, ({ fieldTreeOf }) => this._rivinTila(fieldTreeOf(rivi)).vastatiliPoissa())
      validoiReaktiivisesti(rivi.oletusVastatili, ctrl => this._validateVastatili(ctrl))
      validateTree(rivi, ({ index, valueOf, fieldTree }) => {
        valueOf(rivi.oletusVastatili)
        this._vastatilienVersio()
        // Muiden maksutapojen vastatilit tarkistetaan uudelleen vain jonkin vastatilin muuttuessa
        return untracked(() => this._onkoVastatiliKaytossa(index())) ? { kind: 'vastatiliKaytossa', fieldTree: fieldTree.oletusVastatili as ReadonlyFieldTree<unknown> } : null
      })
      required(rivi.oletusVastatili)
      disabled(rivi.tilitapahtumatSynkronoidaanAlkaen, ({ fieldTreeOf }) => this._rivinTila(fieldTreeOf(rivi)).alkaenPoissa())
      required(rivi.tilitapahtumatSynkronoidaanAlkaen, { when: ({ fieldTreeOf }) => !this._rivinTila(fieldTreeOf(rivi)).alkaenPoissa() })
      disabled(rivi.tilitapahtumatSynkronoidaanLoppuen, ({ fieldTreeOf }) => this._rivinTila(fieldTreeOf(rivi)).loppuenPoissa())
      disabled(rivi.omallaRahallaUid) // maksutapa.maksutapa.kirjauksienLuontityyppi !== KirjauksienLuontityyppi.TOSITTEISTA_GENEROITAVA_TILIOTE  // TODO! Uncomment once omaRahallaUid logic is tested
    })
  })
  maksuConfigLomake: FieldTree<MaksuConfigsLomake> = form(signal<MaksuConfigsLomake>({ hyvaksyjienLukumaara: null, configs: [] }), polku => {
    required(polku.hyvaksyjienLukumaara, { when: () => this._hyvaksyjienLukumaaraPakollinen() })
    validoiReaktiivisesti(polku.hyvaksyjienLukumaara, Validators.min(1))
    applyEach(polku.configs, config => {
      required(config.debtorIdentifier)
      required(config.start)
      // Alku ja loppu tarkistetaan toisiaan vasten vain oman arvon muuttuessa
      validate(config.start, ({ value, valueOf }) =>
        this._maksuConfigAlkaaEnnenLoppua(
          value(),
          untracked(() => valueOf(config.end)),
        ),
      )
      validate(config.end, ({ value, valueOf }) =>
        this._maksuConfigAlkaaEnnenLoppua(
          untracked(() => valueOf(config.start)),
          value(),
        ),
      )
      required(config.iban)
      validoiReaktiivisesti(config.iban, ctrl => this._validateIban(ctrl))
      required(config.bank)
    })
  })
  asiakas: Asiakas
  maksutavatJaKayttajat: MaksutapaJaKayttajat[]
  kaikkiKayttajatObservable: Observable<Kayttaja[]>

  holviOmallaRahallaTili: number = null

  constructor(
    private _errorHandler: ErrorHandler,
    private _changeDetectorRef: ChangeDetectorRef,
    private _asiakasService: AsiakasService,
    private _asiakasKopioija: AsiakasKopioija,
    private _validationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _tilikarttaService: TilikarttaService,
    private _snackbar: MatSnackBar,
    private _bicService: BicService,
    private _stringService: StringService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _firebaseLemonator: FirebaseLemonator,
    private _ibanService: IbanService,
    private _dateService: DateService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _dialog: MatDialog,
    private _tilikarttaJaettuService: TilikarttaJaettuService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjanpitoJaettuService: KirjanpitoJaettuService,
    private _ajastettuTyoService: AjastettuTyoService,
    private _timestampService: TimestampProviderBase,
  ) {}

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnInit() {
    this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(value => {
      this._kirjanpitajaOnDevaajaTaiTuki = value
      if (value) {
        // Override disable if devaaja or tuki
        for (const rivi of this.lomake.maksutavat) {
          const tila = this._rivienTilat.get(rivi)
          this._asetaPoissa(rivi.kirjaustenLuontityyppi, tila.luontityyppiPoissa, false)
          this._asetaPoissa(rivi.sahkoisenTiliotteenTunniste, tila.tilinumeroPoissa, false)
        }
      }
    })

    this.asiakkaanApixTiedotObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(avainTiedot => {
        if (avainTiedot?.avain) {
          const uri = 'customers/' + avainTiedot.avain + '/apix-received-invoice-config/' + avainTiedot.avain
          return this._firebaseLemonaid
            .firestoreDoc<ApixReceivedInvoiceConfig>(uri)
            .listen()
            .pipe(map(data => data || {}))
        }
        return of<Partial<ApixReceivedInvoiceConfig>>({})
      }),
    )

    this._maksutavatChangedSubject.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      const holviApiMaksutavatExist = this.maksutavatJaKayttajat.filter(mt => mt.maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.HOLVI_API).length > 0
      this.isHolviUlkopTositteetVisible = holviApiMaksutavatExist

      const luodaanTositteistaMaksutavatExist = this.maksutavatJaKayttajat.filter(mt => mt.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.TOSITTEISTA_GENEROITAVA_TILIOTE).length > 0
      this.isOmallaRahallaColumnVisible = luodaanTositteistaMaksutavatExist
    })

    this.naytaMaksutilitObservable = combineLatest([this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable, this._kirjautunutKayttajaService.kirjanpitajaOnLuottoObservable]).pipe(
      map(([devaaja, luotto]) => devaaja || luotto),
    )

    this.maksutavatUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return 'asiakkaat/' + avainTiedot.avain + '/maksutavat'
        }
        return ''
      }),
    )
    this.maksutavatEncodedUriObservable = this.maksutavatUriObservable.pipe(map(uri => this._debugService.createFirestoreLink(uri)))
    this.maksutavatLemonaidCustomersUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return 'customers/' + avainTiedot.avain
        }
        return ''
      }),
    )
    this.maksutavatLemonaidCustomersEncodedUriObservable = this.maksutavatLemonaidCustomersUriObservable.pipe(map(uri => this._debugService.createFirestoreLinkLemonaid(uri)))

    this.kaikkiKayttajatObservable = this._asiakasService.nykyisenAsiakkaanKayttajatObservable

    this.maksutavatLemonaidRealtimeDbUriObservable = this._asiakasService.nykyisenAsiakkaanKayttajatObservable.pipe(
      map(kayttajat => {
        if (kayttajat) {
          return kayttajat.map(kayttaja => {
            return {
              kayttaja: kayttaja.etunimi + ' ' + kayttaja.sukunimi,
              uri: 'p/' + kayttaja.avain,
              encodedUri: this._debugService.createRealtimeDatabaseLinkLemonaidUsa('p/' + kayttaja.avain),
            }
          })
        }
        return []
      }),
    )

    this.bankConnectionsUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return 'customers/' + avainTiedot.avain + '/bank-connections/'
        }
        return ''
      }),
    )
    this.bankConnectionsEncodedUriObservable = this.bankConnectionsUriObservable.pipe(map(uri => this._debugService.createFirestoreLinkLemonaid(uri)))

    arvonMuutokset(this.maksuConfigLomake.hyvaksyjienLukumaara, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        if (this.asiakas) {
          this.asiakas.paymentsNumberOfApprovalsRequired = val
        }
      })

    this.asiakkaanOstolaskujenMaksajaRoolinKayttajatObservable = this._asiakasService.nykyisenAsiakkaanKayttajatObservable.pipe(
      map(kayttajat => kayttajat?.filter(kayttaja => kayttaja.aktiivinen && kayttaja.roolit.MAKSUT_MAKSAJA) ?? []),
    )

    this.asiakkaanOstolaskujenHyvaksyjaRoolinKayttajatObservable = this._asiakasService.nykyisenAsiakkaanKayttajatObservable.pipe(
      map(kayttajat => kayttajat?.filter(kayttaja => kayttaja.aktiivinen && kayttaja.roolit.MAKSUT_HYVAKSYJA) ?? []),
    )

    this.asiakkaanOstolaskujenHyvaksyjaRoolinKayttajatObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(hyvaksyjat => {
      if (hyvaksyjat?.length > 0) {
        this._hyvaksyjienLukumaaraPakollinen.set(true)
      } else {
        this._hyvaksyjienLukumaaraPakollinen.set(false)
      }
    })

    this._kaikkiKirjanpitotilitObservable = this._tilikarttaService.nykyisenAsiakkaanTilikartanJaPaatilikartanTilitObservable.pipe(
      map(tilit => {
        return tilit.filter(tili => {
          return tili.numero.length > 3
        })
      }),
      tap(tilit => {
        this._sallitutKirjanpitotilinumerot = new Set(tilit.map(tili => tili.numero))
      }),
      lemonShare(),
    )

    const bankConnectionsObservable: Observable<BankConnection[]> = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(avainTiedot => {
        if (!avainTiedot || !avainTiedot.avain) {
          return observableOf<BankConnection[]>([])
        }
        this.lataaIbaneja.set(true)

        const collection = 'customers/' + avainTiedot.avain + '/bank-connections/'
        return this._firebaseLemonaid.firestoreCollection<BankConnection>(collection).listen()
      }),
      lemonShare(),
    )

    this.asiakkaanIbanitJaPankitObservable = bankConnectionsObservable.pipe(
      map(bankConnections => {
        const output: PankkiJaIban[] = []

        for (const bankConn of bankConnections) {
          if (bankConn.isActive) {
            output.push({
              pankki: this._bicService.annaPankinNimi(bankConn.iban) || 'Tuntematon',
              iban: bankConn.iban,
              bic: this._bicService.annaBicKoodi(bankConn.iban) || 'Tuntematon',
              source: bankConn.source,
              disabledInLemonator: bankConn.disabledInLemonator,
            })
          }
        }

        return output
      }),
      tap(() => this.lataaIbaneja.set(false)),
    )

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeWhile(asiakas => !asiakas, true)).subscribe(asiakas => {
      if (asiakas) {
        this._changeDetectorRef.markForCheck()
        if (!asiakas.tilikaudet?.length) {
          this.eiTilikausia = true
          this.asiakas = this._asiakasKopioija.kopioiAsiakas(asiakas)
          return
        }
        const tanaan = this._dateService.currentLocalDate()
        const tilikausi = this._kirjanpitoJaettuService.annaTilikausiPaivalleTaiEdellinen(asiakas, tanaan) || asiakas.tilikaudet[asiakas.tilikaudet.length - 1]
        if (tilikausi) {
          this._tilikarttaService.asetaAktiivinenTilikausiKonteksti(tilikausi, { year: tanaan.year, month: tanaan.month })
        }
        this.asiakas = this._asiakasKopioija.kopioiAsiakas(asiakas)
        this._alustaMaksuConfigLomakkeenTiedot(this.asiakas)
        this.maksatustunnusEhdotus = asiakas?.ytunnus ? '0' + asiakas.ytunnus.replace('-', '') : ''
      }
    })

    combineLatest([
      this._asiakasService.nykyisenAsiakkaanMaksutavatObservable, // .pipe(tap(() => console.log('Maksutavat changed'))),
      this._asiakasService.nykyisenAsiakkaanKayttajatObservable, // .pipe(tap(() => console.log('Käyttäjät changed'))),
      this._kaikkiKirjanpitotilitObservable, // .pipe(tap(() => console.log('Tilit changed'))),
    ])
      .pipe(
        withLatestFrom(this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiObservable),
        tap(() => {
          this.lataaMaksutapoja = true
        }),
        map(([[maksutavat, kayttajat, kirjanpitotilit], onkoDevaajaTaiTuki]) => {
          const tulokset: MaksutapaJaKayttajat[] = []
          if (maksutavat) {
            for (const maksutapa of maksutavat) {
              const maksutapaJaKayttajat: MaksutapaJaKayttajat = {
                kayttajat: [],
                maksutapa: maksutapa,
                uusi: false,
                onkoValtuutus: null,
              }

              if (maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.HOLVI_SAHKOINEN_TILIOTE && !onkoDevaajaTaiTuki) {
                this.isHolviApiSelectionDisabled = true
              }

              if (
                maksutapa.sahkoisenTiliotteenTunniste &&
                (maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.SAHKOINEN_TILIOTE || maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.MAKSUKORTIN_SAHKOINEN_TILIOTE)
              ) {
                maksutapaJaKayttajat.onkoValtuutus = false
              }

              if (kayttajat) {
                for (const kayttaja of kayttajat) {
                  if (kayttaja.maksutavat) {
                    for (const m of kayttaja.maksutavat) {
                      if (m === maksutapa.tunniste) {
                        maksutapaJaKayttajat.kayttajat.push(kayttaja)
                        break
                      }
                    }
                  }
                }
              }

              maksutapaJaKayttajat.kayttajat.sort((a, b) => {
                if (a.sukunimi !== b.sukunimi) {
                  return a.sukunimi.localeCompare(b.sukunimi)
                }
                return a.etunimi.localeCompare(b.etunimi)
              })

              tulokset.push(maksutapaJaKayttajat)
            }
          }
          tulokset.sort((a, b) => {
            return a.maksutapa.tunniste - b.maksutapa.tunniste
          })
          return { maksutavat: tulokset, kirjanpitotilit: kirjanpitotilit }
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(result => {
        this.maksutavatJaKayttajat = result.maksutavat
        this._lisaaMaksutavatJaOletus(result.maksutavat, result.kirjanpitotilit)
        this.lataaMaksutapoja = false
        this._maksutavatChangedSubject.next()
        this._changeDetectorRef.markForCheck()
      })
  }

  private _rivinTila(rivi: object) {
    return this._rivienTilat.get(rivi)
  }

  private _asetaPoissa<T>(kentta: FieldTree<T>, poissa: WritableSignal<boolean>, arvo: boolean) {
    poissa.set(arvo)
    // Käytöstilan vaihto lähettää kentän arvon tilaajille
    asetaArvo(kentta, kentta().value())
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string) {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string) {
    const tila = kentta()
    return this.onkoVirhe(kentta, laji) && (tila.dirty() || tila.touched())
  }

  annaPankinNimi(iban: string) {
    return this._bicService.annaPankinNimi(iban)
  }

  private _lopetaMaksutavanRivi(rivi: FieldTree<MaksutapaLomake>) {
    const tila = this._rivienTilat.get(rivi)
    tila.lopetus.next()
    tila.lopetus.complete()
  }

  poistaMaksutapa(index: number) {
    this._lopetaMaksutavanRivi(this.lomake.maksutavat[index])
    asetaArvo(
      this.lomake.maksutavat,
      this.lomake
        .maksutavat()
        .value()
        .filter((_, i) => i !== index),
      { ilmoita: false },
    )
    this.maksutavatJaKayttajat.splice(index, 1)

    // Järjestä tunnisteet uudelleen uusien osalta
    const vanhat = this.maksutavatJaKayttajat.filter(a => {
      return !a.uusi
    })
    const uudet = this.maksutavatJaKayttajat.filter(a => {
      return a.uusi
    })
    for (const uusiMaksutapa of uudet) {
      uusiMaksutapa.maksutapa.tunniste = this.annaSeuraavaMaksutavanNumero(vanhat)
      vanhat.push(uusiMaksutapa)
    }
    this._maksutavatChangedSubject.next()
  }

  lisaaMaksutapa() {
    this._kaikkiKirjanpitotilitObservable.pipe(take(1)).subscribe(tilit => {
      const seuraavaNumero = this.annaSeuraavaMaksutavanNumero(this.maksutavatJaKayttajat)
      const lisattava: AsiakkaanMaksutapa = {
        avain: null,
        aktiivinen: true,
        base64Kuva: this._defaultMaksutapaKuva,
        nimi: '',
        tunniste: seuraavaNumero,
        kirjauksienLuontityyppi: null,
        sahkoisenTiliotteenTunniste: null,
        oletusVastatili: null,
        tilitapahtumatSynkronoidaanAlkaen: null,
        tilitapahtumatSynkronoidaanLoppuen: null,
      }

      const maksutapaJaKayttajat: MaksutapaJaKayttajat = {
        kayttajat: [],
        maksutapa: lisattava,
        uusi: true,
        onkoValtuutus: null,
      }

      this.maksutavatJaKayttajat.push(maksutapaJaKayttajat)
      this._lisaaMaksutavanRivi(maksutapaJaKayttajat, tilit)
    })
  }

  private customMaksutapatunnisteet: number[] = [
    MAKSUTAPA_MYYNTILASKU.tunniste,
    MAKSUTAPA_PALKKATOSITE.tunniste,
    MAKSUTAPA_MYYNTITOSITE.tunniste,
    MAKSUTAPA_TILIOTETOSITE.tunniste,
    MAKSUTAPA_MUU.tunniste,
    MAKSUTAPA_SAHKOISET_LASKUT.tunniste,
    MAKSUTAPA_SAHKOISET_LASKUT_FAKE.tunniste,
  ]

  private annaSeuraavaMaksutavanNumero(maksutavatJaKayttajat: MaksutapaJaKayttajat[]): number {
    let suurin = 0
    if (maksutavatJaKayttajat) {
      for (const maksutapaJaKayttajat of maksutavatJaKayttajat) {
        if (!this.customMaksutapatunnisteet.includes(maksutapaJaKayttajat.maksutapa.tunniste) && maksutapaJaKayttajat.maksutapa.tunniste > suurin) {
          suurin = maksutapaJaKayttajat.maksutapa.tunniste
        }
      }

      suurin++

      while (this.customMaksutapatunnisteet.includes(suurin)) {
        suurin++
      }
    }
    return suurin
  }

  private _lisaaMaksutavatJaOletus(maksutavat: MaksutapaJaKayttajat[], kirjanpitotilit: Kirjanpitotili[]) {
    // Tyhjennä array
    for (const rivi of this.lomake.maksutavat) {
      this._lopetaMaksutavanRivi(rivi)
    }
    asetaArvo(this.lomake.maksutavat, [], { ilmoita: false })

    // Lisää kaikki
    for (const maksutapa of maksutavat) {
      if (maksutapa.maksutapa.holviOmallaRahallaAccount) {
        this.holviOmallaRahallaTili = maksutapa.maksutapa.tunniste
      }

      this._lisaaMaksutavanRivi(maksutapa, kirjanpitotilit)
    }
  }

  compareLuontityypit(tyyppi1: LokalisoituTiliotetyyppi, tyyppi2: LokalisoituTiliotetyyppi): boolean {
    return tyyppi1?.tunniste === tyyppi2?.tunniste && tyyppi1?.alitunniste === tyyppi2?.alitunniste
  }

  private _annaKirjaustenLuontityypit(): LokalisoituTiliotetyyppi[] {
    const sahkoinenTiliote = (alatyyppi: SahkoisenTiliotteenAlatyyppi): LokalisoituTiliotetyyppi => ({
      nimi: this._kirjanpitoJaettuService.annaMaksutavanLuontityypinNimi(KirjauksienLuontityyppi.SAHKOINEN_TILIOTE, alatyyppi),
      tunniste: KirjauksienLuontityyppi.SAHKOINEN_TILIOTE,
      alitunniste: alatyyppi,
    })
    const muuTiliote = (luontityyppi: KirjauksienLuontityyppi): LokalisoituTiliotetyyppi => ({
      nimi: this._kirjanpitoJaettuService.annaMaksutavanLuontityypinNimi(luontityyppi),
      tunniste: luontityyppi,
      alitunniste: null,
    })

    return [
      { ...sahkoinenTiliote(SahkoisenTiliotteenAlatyyppi.AIIA), poistunutKaytosta: true },
      sahkoinenTiliote(SahkoisenTiliotteenAlatyyppi.HOLVI_API),
      sahkoinenTiliote(SahkoisenTiliotteenAlatyyppi.ENABLE_BANKING),
      sahkoinenTiliote(SahkoisenTiliotteenAlatyyppi.WEB_SERVICES),
      muuTiliote(KirjauksienLuontityyppi.TOSITTEISTA_GENEROITAVA_TILIOTE),
      muuTiliote(KirjauksienLuontityyppi.KASIN_SYOTETTAVA_TILIOTE),
      muuTiliote(KirjauksienLuontityyppi.HOLVI_SAHKOINEN_TILIOTE),
    ]
  }

  private _lisaaMaksutavanRivi(maksutapa: MaksutapaJaKayttajat, kirjanpitotilit: Kirjanpitotili[]) {
    const luontityyppiJaIbanDisabled =
      !this._kirjanpitajaOnDevaajaTaiTuki &&
      maksutapa.maksutapa.sahkoisenTiliotteenTunniste &&
      [KirjauksienLuontityyppi.SAHKOINEN_TILIOTE, KirjauksienLuontityyppi.MAKSUKORTIN_SAHKOINEN_TILIOTE].includes(maksutapa.maksutapa.kirjauksienLuontityyppi)
    const tilinumeroDisabled = !(
      maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.SAHKOINEN_TILIOTE || maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.MAKSUKORTIN_SAHKOINEN_TILIOTE
    )
    const tilitapahtumatSynkronoidaanAikaDisabled = !(
      maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.SAHKOINEN_TILIOTE ||
      maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.MAKSUKORTIN_SAHKOINEN_TILIOTE ||
      maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.HOLVI_SAHKOINEN_TILIOTE
    )
    const vastakirjausDisabled = maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.KASIN_SYOTETTAVA_TILIOTE

    const luontityyppiKey: string = maksutapa.maksutapa.kirjauksienLuontityyppi + '_' + (maksutapa.maksutapa.sahkoisenTiliotteenAlatyyppi || null)
    const luontityyppi = this._kirjaustenLuontityypitByTunnisteetMap.get(luontityyppiKey)

    const rivit = this.lomake.maksutavat().value()
    asetaArvo(
      this.lomake.maksutavat,
      [
        ...rivit,
        {
          nimi: maksutapa.maksutapa.nimi ?? '',
          kirjaustenLuontityyppi: luontityyppi ?? null,
          sahkoisenTiliotteenTunniste: maksutapa.maksutapa.sahkoisenTiliotteenTunniste ?? null,
          oletusVastatili: maksutapa.maksutapa.oletusVastatili ?? null,
          aktiivinen: maksutapa.maksutapa.aktiivinen ?? null,
          tilitapahtumatSynkronoidaanAlkaen: this._dateService.localDateToDate(maksutapa.maksutapa.tilitapahtumatSynkronoidaanAlkaen) ?? null,
          tilitapahtumatSynkronoidaanLoppuen: this._dateService.localDateToDate(maksutapa.maksutapa.tilitapahtumatSynkronoidaanLoppuen) ?? null,
          omallaRahallaUid: maksutapa?.maksutapa?.omallaRahallaUid || null,
        },
      ],
      { ilmoita: false },
    )
    const rivi = this.lomake.maksutavat[rivit.length]
    const tila: MaksutapaRivinTila = {
      luontityyppiPoissa: signal(!!luontityyppiJaIbanDisabled),
      tilinumeroPoissa: signal(!!(tilinumeroDisabled || luontityyppiJaIbanDisabled)),
      // Tilinumero on aluksi pakollinen myös silloin, kun kenttä ei ole käytössä
      tilinumeronValidointi: signal<TilinumeronValidointi>({ iban: true, pakollinen: true, bicAlatyyppi: undefined }),
      alkaenPoissa: signal(tilitapahtumatSynkronoidaanAikaDisabled),
      loppuenPoissa: signal(tilitapahtumatSynkronoidaanAikaDisabled),
      vastatiliPoissa: signal(vastakirjausDisabled),
      lopetus: new Subject<void>(),
    }
    this._rivienTilat.set(rivi, tila)
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(tila.lopetus))
    const sahkoisenTiliotteenTunnisteKentta = rivi.sahkoisenTiliotteenTunniste
    const tilitapahtumatSynkronoidaanAlkaenKentta = rivi.tilitapahtumatSynkronoidaanAlkaen
    const tilitapahtumatSynkronoidaanLoppuenKentta = rivi.tilitapahtumatSynkronoidaanLoppuen
    const oletusVastatiliKentta = rivi.oletusVastatili

    muutokset(rivi.nimi)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        maksutapa.maksutapa.nimi = this._validationService.processValue(value)
      })

    muutokset(rivi.kirjaustenLuontityyppi)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((value: LokalisoituTiliotetyyppi) => {
        maksutapa.maksutapa.kirjauksienLuontityyppi = value?.tunniste || null
        const alatyyppi = value?.alitunniste || null

        if (alatyyppi) {
          maksutapa.maksutapa.sahkoisenTiliotteenAlatyyppi = alatyyppi
        } else {
          delete maksutapa.maksutapa.sahkoisenTiliotteenAlatyyppi
        }

        if (
          maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.SAHKOINEN_TILIOTE ||
          maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.MAKSUKORTIN_SAHKOINEN_TILIOTE
        ) {
          tila.tilinumeronValidointi.set({ iban: true, pakollinen: true, bicAlatyyppi: alatyyppi })
          this._asetaPoissa(sahkoisenTiliotteenTunnisteKentta, tila.tilinumeroPoissa, false)

          this._asetaPoissa(tilitapahtumatSynkronoidaanAlkaenKentta, tila.alkaenPoissa, false)

          this._asetaPoissa(tilitapahtumatSynkronoidaanLoppuenKentta, tila.loppuenPoissa, false)
        } else if (maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.HOLVI_SAHKOINEN_TILIOTE) {
          this._asetaPoissa(tilitapahtumatSynkronoidaanAlkaenKentta, tila.alkaenPoissa, false)

          asetaArvo(sahkoisenTiliotteenTunnisteKentta, null)
          tila.tilinumeronValidointi.set({ iban: false, pakollinen: false, bicAlatyyppi: undefined })
          this._asetaPoissa(sahkoisenTiliotteenTunnisteKentta, tila.tilinumeroPoissa, true)

          this._asetaPoissa(tilitapahtumatSynkronoidaanLoppuenKentta, tila.loppuenPoissa, false)
        } else {
          asetaArvo(tilitapahtumatSynkronoidaanAlkaenKentta, null)
          this._asetaPoissa(tilitapahtumatSynkronoidaanAlkaenKentta, tila.alkaenPoissa, true)

          asetaArvo(sahkoisenTiliotteenTunnisteKentta, null)
          tila.tilinumeronValidointi.set({ iban: false, pakollinen: false, bicAlatyyppi: undefined })
          this._asetaPoissa(sahkoisenTiliotteenTunnisteKentta, tila.tilinumeroPoissa, true)

          asetaArvo(tilitapahtumatSynkronoidaanLoppuenKentta, null)
          this._asetaPoissa(tilitapahtumatSynkronoidaanLoppuenKentta, tila.loppuenPoissa, true)
        }

        // if (maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.TOSITTEISTA_GENEROITAVA_TILIOTE) {
        //   omallaRahallaUidControl.enable() // TODO! Uncomment once tested
        // }

        if (maksutapa.maksutapa.kirjauksienLuontityyppi === KirjauksienLuontityyppi.KASIN_SYOTETTAVA_TILIOTE) {
          asetaArvo(oletusVastatiliKentta, null)
          this._asetaPoissa(oletusVastatiliKentta, tila.vastatiliPoissa, true)
        } else {
          this._asetaPoissa(oletusVastatiliKentta, tila.vastatiliPoissa, false)
        }

        this._maksutavatChangedSubject.next()
      })

    muutokset(sahkoisenTiliotteenTunnisteKentta)
      .pipe(takeUntil(this._ngUnsubscribe))
      .pipe(withLatestFrom(this.asiakkaanIbanitJaPankitObservable))
      .subscribe(([value, ibanitJaPankit]) => {
        maksutapa.maksutapa.sahkoisenTiliotteenTunniste = this._stringService.removeAllWhiteSpaces(value)
        maksutapa.onkoValtuutus = this._onkoValtuutus(maksutapa.maksutapa, ibanitJaPankit)
      })

    this.asiakkaanIbanitJaPankitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(ibanitJaPankit => {
      maksutapa.onkoValtuutus = this._onkoValtuutus(maksutapa.maksutapa, ibanitJaPankit)
      this._changeDetectorRef.markForCheck()
    })

    muutokset(rivi.aktiivinen)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        maksutapa.maksutapa.aktiivinen = this._validationService.processValue(value)
      })

    muutokset(tilitapahtumatSynkronoidaanAlkaenKentta)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        maksutapa.maksutapa.tilitapahtumatSynkronoidaanAlkaen = this._dateService.dateToLocalDate(value)
      })

    muutokset(tilitapahtumatSynkronoidaanLoppuenKentta)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        maksutapa.maksutapa.tilitapahtumatSynkronoidaanLoppuen = this._dateService.dateToLocalDate(value)
      })

    muutokset(rivi.omallaRahallaUid)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        if (value) {
          maksutapa.maksutapa.omallaRahallaUid = value
        } else {
          delete maksutapa.maksutapa.omallaRahallaUid
        }
      })

    muutokset(oletusVastatiliKentta)
      .pipe(debounceTime(150), takeUntil(this._ngUnsubscribe))
      .subscribe((value: any) => {
        if (value && value.numero) {
          // Huom, objekti tunnistetaan numerosta!
          maksutapa.maksutapa.oletusVastatili = value.numero
        } else if (value) {
          let setNull = true
          const trimmattu = value.trim()
          for (const tili of kirjanpitotilit) {
            if (tili.numero === trimmattu) {
              maksutapa.maksutapa.oletusVastatili = tili.numero
              setNull = false
              break
            }
          }
          if (setNull) {
            maksutapa.maksutapa.oletusVastatili = null
          }
        } else {
          maksutapa.maksutapa.oletusVastatili = null
        }
      })

    muutokset(oletusVastatiliKentta)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        this._vastatilienVersio.update(versio => versio + 1)
      })

    muutokset(oletusVastatiliKentta)
      .pipe(
        debounceTime(50),
        startWith<string | Kirjanpitotili>(''),
        map(value => {
          if (typeof value === 'string') {
            return value
          }
          if (value) {
            return value.numero
          }
          return null
        }),
        map(name => (name ? this._filterTili(name, kirjanpitotilit) : [])),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(value => {
        this.suodatetutKirjanpitotilitSubject.next(value)
      })
  }

  lisaaMaksutavanKuva(index: number) {
    this._lisaaKuvaIndeksiin = index
    const dialogRef = this._dialog.open<AsiakkaanMaksutavatValitseKuvakeDialog, void, string>(AsiakkaanMaksutavatValitseKuvakeDialog)
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(valittu => {
        if (valittu) {
          this.maksutavatJaKayttajat[this._lisaaKuvaIndeksiin].maksutapa.base64Kuva = valittu
          this._changeDetectorRef.markForCheck()
        }
      })
  }

  holviOmallaRahallaTiliChanged(val: number) {
    if (val === this.holviOmallaRahallaTili) {
      this.holviOmallaRahallaTili = null
    } else {
      this.holviOmallaRahallaTili = val
    }
    this.commonError.set(null)
  }

  private _filterTili(numeroTaiNimi: string, kirjanpitotilit: Kirjanpitotili[]): Kirjanpitotili[] {
    const filterValue = numeroTaiNimi.toLowerCase()
    return kirjanpitotilit.filter(
      tili => tili.numero.toLowerCase().indexOf(filterValue) === 0 || this._tilikarttaJaettuService.annaKirjanpitotilinNimi(tili, 'fi').toLowerCase().indexOf(filterValue) === 0,
    )
  }
  private _validateVastatili: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    if (!this._sallitutKirjanpitotilinumerot.has(ctrl.value)) {
      return { notkirjanpitotili: true }
    }
  }

  private _onkoVastatiliKaytossa(indeksi: number) {
    const rivit = this.lomake.maksutavat
    const vastatili = this._annaVastatiliKentanArvosta(rivit[indeksi].oletusVastatili().value())
    if (!vastatili) {
      return false
    }
    let i = 0
    for (const rivi of rivit) {
      const vastatiliKentta = rivi.oletusVastatili()
      if (i !== indeksi && !vastatiliKentta.disabled() && this._annaVastatiliKentanArvosta(vastatiliKentta.value()) === vastatili) {
        return true
      }
      i++
    }
    return false
  }

  private _annaVastatiliKentanArvosta(value: any): string | null {
    const vastatili = typeof value === 'string' ? value : value?.numero
    return vastatili?.trim() || null
  }

  private _validateIban: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    if (!this._ibanService.isValidIban(ctrl.value)) {
      return { invalidiban: true }
    }
  }

  displayTiliFn(tili?: Kirjanpitotili): string | undefined {
    return tili ? tili.numero + ' ' + this._tilikarttaJaettuService.annaKirjanpitotilinNimi(tili, 'fi') : undefined
  }

  async save() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    const maksutavat = this.maksutavatJaKayttajat.map(a => a.maksutapa)
    const holviApiMaksutavat = maksutavat.filter(mt => mt.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.HOLVI_API)

    if (holviApiMaksutavat.length && !this.holviOmallaRahallaTili) {
      this.commonError.set('Valitse maksutapa, jossa "Kirjauksien luontitapa" on "Luodaan tositteista", johon laitetaan Holvista tulevat ulkopuoliset tositteet.')
      return
    }

    if (maksutavat.filter(m => !m.base64Kuva || m.base64Kuva === this._defaultMaksutapaKuva).length > 0) {
      this.commonError.set('Valitse kaikille maksutavoille kuvake napauttamalla maksutavan kuvaketta.')
      return
    }

    if (!!this.saveStarted) {
      return
    }
    this.saveStarted = true
    this.commonError.set(null)

    this._ladataanService.aloitaLataaminen()

    const ibanitJaPankit = await firstValueFrom(this.asiakkaanIbanitJaPankitObservable)
    const connectedIbans = ibanitJaPankit.map(ijp => ijp.iban)

    for (const maksutapa of maksutavat) {
      if (maksutapa.tunniste === this.holviOmallaRahallaTili) {
        maksutapa.holviOmallaRahallaAccount = true
      } else {
        delete maksutapa.holviOmallaRahallaAccount
      }
    }

    const areAllAccessibleAccountsInMaksutavat: boolean = this._areAllConnectedIbansSetToMaksutavat(connectedIbans, maksutavat)

    return this._asiakasService
      .tallennaMaksutavat(this.asiakas, maksutavat, areAllAccessibleAccountsInMaksutavat)
      .then(() => {
        this._snackbar.open('Maksutavat tallennettiin.', 'OK', { duration: 2500, verticalPosition: 'top' })
      })
      .catch(err => {
        this._errorHandler.handleError(err)
        this.commonError.set('Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpidolle. Tekninen virhe: "' + err.message + '".')
      })
      .finally(() => {
        this._ladataanService.lopetaLataaminen()
        this.saveStarted = false
      })
  }
  private _areAllConnectedIbansSetToMaksutavat(connectedIbans: string[], maksutavat: AsiakkaanMaksutapa[]): boolean {
    const maksutapojenTilit = maksutavat.map(mt => mt.sahkoisenTiliotteenTunniste)
    for (const connected of connectedIbans) {
      if (!maksutapojenTilit.includes(connected)) {
        return false
      }
    }
    return true
  }

  async disableConnectedAccount(pankkiJaIban: PankkiJaIban) {
    this.disableOrEnableConnectedAccount(pankkiJaIban, 'disable')
  }

  enableConnectedAccount(pankkiJaIban: PankkiJaIban) {
    this.disableOrEnableConnectedAccount(pankkiJaIban, 'activate')
  }

  private async disableOrEnableConnectedAccount(pankkiJaIban: PankkiJaIban, action: 'disable' | 'activate') {
    this._ladataanService.aloitaLataaminen()

    try {
      const requestData: BankConnectionDisableRequest = {
        asiakasAvain: this.asiakas.avain,
        iban: pankkiJaIban.iban,
        action: action,
      }
      const resp = await this._firebaseLemonator.functionsCall<BankConnectionDisableRequest, BankConnectionDisableResponse>('bankConnectionDisableOrActivateManually', requestData)

      if (!resp || resp.e) {
        throw new Error(resp.e ?? 'No response')
      }

      // Don't do anything if successful - the BankConnection will be updated in the observable chain.
    } catch (err) {
      this._errorHandler.handleError(new Error('Failed to disable bank connection manually: ' + err?.message))
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private _maksuConfigAlkaaEnnenLoppua(a: Date | null, l: Date | null) {
    if (l && a && a >= l) {
      return { kind: 'loppuEnnenAlkua' }
    }
    return null
  }

  private _lisaaMaksuConfiginRivi(config: LemonatorPaymentConfig, focus: boolean) {
    const configit = this.maksuConfigLomake.configs().value()
    asetaArvo(
      this.maksuConfigLomake.configs,
      [
        ...configit,
        {
          debtorIdentifier: config.debtorIdentifier ?? '',
          start: this._dateService.numberToDate(config.start),
          end: this._dateService.numberToDate(config.end),
          iban: config.iban ?? null,
          bank: config.bank ?? null,
        },
      ],
      { ilmoita: false },
    )
    const configRivi = this.maksuConfigLomake.configs[configit.length]
    const lopetus = new Subject<void>()
    this._maksuConfigienLopetukset.push(lopetus)
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(lopetus))

    muutokset(configRivi.debtorIdentifier)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        config.debtorIdentifier = val
      })

    muutokset(configRivi.bank)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        config.bank = val
      })

    muutokset(configRivi.iban)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(val => {
        if (!configRivi.bank().value() && this._ibanService.isValidIban(val)) {
          const bic = this._bicService.annaBicKoodi(val)

          if (validBanksForBic[bic]?.length === 1) {
            // Note: ITELFIHH has multiple matches, skipping those too
            asetaArvo(configRivi.bank, validBanksForBic[bic][0])
          }
        }
        config.iban = this._stringService.removeAllWhiteSpaces(val)
      })

    muutokset(configRivi.start)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        config.start = this._dateService.dateToNumber(value)
      })

    muutokset(configRivi.end)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        config.end = this._dateService.dateToNumber(value)
      })

    if (focus) {
      setTimeout(() => {
        if (this.debtorIdentifierit?.last) {
          this.debtorIdentifierit.last.focus()
          this._changeDetectorRef.markForCheck()
        }
      }, 10)
    }
  }

  lisaaMaksuConfig() {
    const config: LemonatorPaymentConfig = {
      avain: this._firebaseLemonator.firestoreCreateId(),
      debtorIdentifier: null,
      end: null,
      start: this._dateService.currentNumberDate(),
      iban: null,
      bank: null,
      type: 'avalo',
    }
    this._lisaaMaksuConfiginRivi(config, true)

    if (!this.asiakas.paymentConfigs) {
      this.asiakas.paymentConfigs = []
    }
    this.asiakas.paymentConfigs.push(config)
  }

  async saveMaksuConfigs() {
    this.commonErrorPaymentConfigs = null

    if (!this.asiakas.paymentConfigs || this.asiakas.paymentConfigs.length < 1) {
      this.commonErrorPaymentConfigs = 'Lisää vähintään yksi maksutili.'
      return
    }

    if (!this.maksuConfigLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.maksuConfigLomake)
      this._validationService.naytaEnsimmainenVirhe()
      return
    }

    this._ladataanService.aloitaLataaminen()

    this._asiakasService
      .paivitaAsiakas(this.asiakas, 'tilikaudet-eivat-voineet-muuttua', false, false)
      .then(() => {
        this._asiakasService.asetaNykyinenAsiakas(this.asiakas)
        this._ladataanService.lopetaLataaminen()
        this._snackbar.open('Asiakas tallennettiin onnistuneesti.', 'OK', { duration: 5000, verticalPosition: 'top' })
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
      })
  }

  async kytkeMaksuominaisuus(event: MatSlideToggleChange, toggle: MatSlideToggle) {
    this._ladataanService.aloitaLataaminen()

    const reqData: AsiakasKytkeMaksuOminaisuusRequest = {
      asiakasAvain: this.asiakas.avain,
      paymentIsActive: event.checked,
    }

    try {
      const resp = await this._firebaseLemonator.functionsCall<AsiakasKytkeMaksuOminaisuusRequest, AsiakasKytkeMaksuOminaisuusResponse>('asiakasKytkeMaksuominaisuus', reqData)
      if (!resp || resp.e) {
        throw Error(`Maksuominaisuuden kytkeminen epäonnistui: ${resp.e}`)
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      const apixTiedot = await firstValueFrom(this.asiakkaanApixTiedotObservable)
      toggle.checked = apixTiedot.paymentIsActive
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private _alustaMaksuConfigLomakkeenTiedot(asiakas: Asiakas) {
    asetaArvo(this.maksuConfigLomake.hyvaksyjienLukumaara, asiakas.paymentsNumberOfApprovalsRequired ?? null)

    for (const lopetus of this._maksuConfigienLopetukset) {
      lopetus.next()
      lopetus.complete()
    }
    this._maksuConfigienLopetukset = []
    asetaArvo(this.maksuConfigLomake.configs, [], { ilmoita: false })
    for (const config of asiakas.paymentConfigs ?? []) {
      this._lisaaMaksuConfiginRivi(config, false)
    }
  }

  asetaMaksatustunnusEhdotus(config: FieldTree<MaksuConfigLomake>, event: MouseEvent) {
    event.preventDefault()
    asetaArvo(config.debtorIdentifier, this.maksatustunnusEhdotus)
  }

  private _onkoValtuutus(maksutapa: AsiakkaanMaksutapa, ibanitJaPankit: PankkiJaIban[]): boolean {
    if (!ibanitJaPankit?.length) {
      return false
    }
    if (maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.AIIA) {
      return ibanitJaPankit.findIndex(ijp => ijp.source === 'aiia' && ijp.iban === maksutapa.sahkoisenTiliotteenTunniste) > -1
    }
    if (maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.HOLVI_API) {
      return ibanitJaPankit.findIndex(ijp => ijp.source === 'holvi' && ijp.iban === maksutapa.sahkoisenTiliotteenTunniste) > -1
    }
    if (maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.ENABLE_BANKING) {
      return ibanitJaPankit.findIndex(ijp => ijp.source === 'enable-banking' && ijp.iban === maksutapa.sahkoisenTiliotteenTunniste) > -1
    }
    return false
  }

  private _alatyyppiAllowedBicsValidator =
    (alatyyppi: SahkoisenTiliotteenAlatyyppi): ValidatorFn =>
    (ctrl: AbstractControl): ValidationErrors | null => {
      if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
        return null
      }
      if (!this._ibanService.isValidIban(ctrl.value)) {
        return null
      }

      const bic = this._bicService.annaBicKoodi(ctrl.value)

      if (alatyyppi === SahkoisenTiliotteenAlatyyppi.HOLVI_API) {
        if (bic === 'HOLVFIHH') {
          return null
        }
        return { invalidbic: this._bicService.annaPankinNimi(ctrl.value) }
      }

      return null
    }

  showMaksutapaHistory(maksutapaIndex: number) {
    const maksutapa = this.maksutavatJaKayttajat[maksutapaIndex].maksutapa

    this._dialog.open(AsiakkaanMaksutavatHistoriaDialog, {
      width: '1200px',
      maxHeight: '90vh',
      data: {
        asiakasAvain: this.asiakas.avain,
        maksutapa: maksutapa,
        kirjanpitajienNimitiedotMapObservable: this.kirjanpitajienNimitiedotMapObservable,
      },
    })
  }
  async startProviderChange(maksutapaIndex: number) {
    const maksutapaJaKayttajat = this.maksutavatJaKayttajat[maksutapaIndex]
    const maksutapa = maksutapaJaKayttajat.maksutapa
    // Check if there's already a scheduled change
    if (maksutapa.scheduledProviderChange) {
      this._snackbar.open('Palveluntarjoajan vaihto on jo ajastettu tälle maksutavalle.', 'OK', { duration: 5000, verticalPosition: 'top' })
      return
    }

    if (maksutapa.sahkoisenTiliotteenAlatyyppi !== SahkoisenTiliotteenAlatyyppi.AIIA && maksutapa.sahkoisenTiliotteenAlatyyppi !== SahkoisenTiliotteenAlatyyppi.ENABLE_BANKING) {
      this._snackbar.open('Palveluntarjoajan vaihto on mahdollista vain Aiia- ja Enable Banking -maksutavoille.', 'OK', { duration: 5000, verticalPosition: 'top' })
      return
    }

    // Check if BankConnection is active
    const ibanitJaPankit = await firstValueFrom(this.asiakkaanIbanitJaPankitObservable)
    const activeBankConnection = ibanitJaPankit.find(
      ijp =>
        ijp.iban === maksutapa.sahkoisenTiliotteenTunniste &&
        ((maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.AIIA && ijp.source === 'aiia') ||
          (maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.ENABLE_BANKING && ijp.source === 'enable-banking')),
    )

    // Determine available providers based on current provider
    const availableProviders: { value: SahkoisenTiliotteenAlatyyppi; label: string }[] = []
    if (maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.AIIA) {
      availableProviders.push({ value: SahkoisenTiliotteenAlatyyppi.ENABLE_BANKING, label: 'Enable Banking' })
    } else if (maksutapa.sahkoisenTiliotteenAlatyyppi === SahkoisenTiliotteenAlatyyppi.ENABLE_BANKING) {
      availableProviders.push({ value: SahkoisenTiliotteenAlatyyppi.AIIA, label: 'Aiia' })
    }

    if (availableProviders.length === 0) {
      this._snackbar.open('Tälle palveluntarjoajalle ei ole vaihtoehtoja.', 'OK', { duration: 5000, verticalPosition: 'top' })
      return
    }

    const viimeinenTilitapahtuma = await this._firebaseLemonaid
      .firestoreCollection<Tilitapahtuma>('customers/' + this.asiakas.avain + '/bank-transactions')
      .where('iban', '==', maksutapa.sahkoisenTiliotteenTunniste)
      .where('tila', '==', 'final')
      .orderBy('p', 'desc')
      .limit(1)
      .get()
      .then(docs => docs?.[0])

    const dialogData: AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialogData = {
      maksutapa: maksutapa,
      hasActiveConnection: !!activeBankConnection,
      latestTransactionDate: viimeinenTilitapahtuma?.pvml,
      availableProviders,
    }

    const dialogRef = this._dialog.open<
      AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialog,
      AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialogData,
      AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialogResult
    >(AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialog, {
      data: dialogData,
      width: '500px',
    })

    const result = await firstValueFrom(dialogRef.afterClosed())
    if (!result) {
      return // User cancelled
    }
    console.log('Scheduling provider change result:', result)

    try {
      const vaihtoTyodata: AktivoiMaksutavanPalveluntarjoajanVaihtoWorkQueue = {
        asiakasAvain: this.asiakas.avain,
        maksutavanAvain: maksutapa.avain,
        created: this._timestampService.now(),
      }

      const batch = this._firebaseLemonator.firestoreBatch()
      const vaihtoTyoAvain = this._firebaseLemonator.firestoreCreateId()
      const vaihtoTyojonoUri = `tyojono/${this.asiakas.avain}/aktivoi-maksutavan-palveluntarjoajan-vaihto/${vaihtoTyoAvain}`

      // Update maksutapa with scheduledProviderChange
      const kirjanpitaja = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
      const scheduledProviderChange: AsiakkaanMaksutapa['scheduledProviderChange'] = {
        uusiAlatyyppi: result.uusiAlatyyppi,
        voimassaAlkaen: this._dateService.localDateToNumber(result.voimassaAlkaen),
        ajastettuTyoId: vaihtoTyoAvain,
        luotu: this._timestampService.now(),
        luoja: kirjanpitaja.uid,
      }
      if (activeBankConnection && result.aktivoiHetiJosYhteysKatkeaa) {
        scheduledProviderChange.aktivoiHetiJosYhteysKatkeaa = true
      }

      if (!activeBankConnection) {
        /** Bank connection currently NOT active, start maksutapa update immediately */
        batch.set(vaihtoTyojonoUri, vaihtoTyodata)
      } else {
        /** Bank connection currently ACTIVE, delay the maksutapa update */
        const firestoreProvider = this._firebaseLemonator.firestoreProvider

        const voimassaAlkaenPaivanAlku: LocalDateTime = { year: result.voimassaAlkaen.year, month: result.voimassaAlkaen.month, day: result.voimassaAlkaen.day, hour: 1, minutes: 0, seconds: 0 }

        const vaihtoAjastettuTyo: AjastettuTyo<AktivoiMaksutavanPalveluntarjoajanVaihtoWorkQueue> = {
          avain: vaihtoTyoAvain,
          asiakasAvain: this.asiakas.avain,
          kohde: vaihtoTyojonoUri,
          tyodata: vaihtoTyodata,
          ajoaika: this._timestampService.localDateTimeToTimestamp(voimassaAlkaenPaivanAlku),
          ajettu: null,
          tyyppi: 'aktivoi-maksutavan-palveluntarjoajan-vaihto',
          selite: `Aktivoi maksutavan ${maksutapa.avain} palveluntarjoajan vaihto: ${maksutapa.sahkoisenTiliotteenAlatyyppi} -> ${result.uusiAlatyyppi}`,
        }

        this._ajastettuTyoService.ajasta(vaihtoAjastettuTyo, firestoreProvider, batch as any)

        if (result.aktivoiHetiJosYhteysKatkeaa) {
          const yhteystarkistusTyoAvain = `${vaihtoTyoAvain}-yhteystarkistus`
          const yhteystarkistusTyojonoUri = `tyojono/${this.asiakas.avain}/palveluntarjoajan-vaihto-yhteystarkistus/${yhteystarkistusTyoAvain}`
          const yhteystarkistusTyodata: PalveluntarjoajanVaihdonYhteystarkistusTyojono = {
            asiakasAvain: this.asiakas.avain,
            maksutavanAvain: maksutapa.avain,
            ajastettuTyoId: vaihtoTyoAvain,
            created: this._timestampService.now(),
          }
          const yhteystarkistusAjastettuTyo: AjastettuTyo<PalveluntarjoajanVaihdonYhteystarkistusTyojono> = {
            avain: yhteystarkistusTyoAvain,
            asiakasAvain: this.asiakas.avain,
            kohde: yhteystarkistusTyojonoUri,
            tyodata: yhteystarkistusTyodata,
            ajoaika: this._timestampService.dateToTimestamp(this._dateService.lisaaPaivia(new Date(), 1)),
            ajettu: null,
            tyyppi: 'palveluntarjoajan-vaihto-yhteystarkistus',
            selite: `Tarkista onko maksutavan ${maksutapa.avain} pankkiyhteys katkennut (aikaistettu palveluntarjoajan vaihto)`,
          }
          this._ajastettuTyoService.ajasta(yhteystarkistusAjastettuTyo, firestoreProvider, batch as any)
        }
      }

      const maksutapaUri = 'asiakkaat/' + this.asiakas.avain + '/maksutavat/' + maksutapa.avain
      const maksutapaUpdateData: Pick<AsiakkaanMaksutapa, 'scheduledProviderChange'> = {
        scheduledProviderChange: scheduledProviderChange,
      }

      batch.update(maksutapaUri, maksutapaUpdateData)

      await batch.commit()

      this._snackbar.open('Palveluntarjoajan vaihto aloitettu onnistuneesti.', 'OK', { duration: 5000, verticalPosition: 'top' })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Palveluntarjoajan vaihto epäonnistui.', 'OK', { duration: 5000, verticalPosition: 'top' })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async avaaPoistaTilitapahtumatDialog(index: number) {
    const iban = this.maksutavatJaKayttajat[index].maksutapa.sahkoisenTiliotteenTunniste

    const dialogData: AsiakkaanMaksutavatPoistaTilitapahtumatDialogData = {
      asiakasAvain: this.asiakas.avain,
      iban,
    }

    const dialogRef = this._dialog.open<AsiakkaanMaksutavatPoistaTilitapahtumatDialog, AsiakkaanMaksutavatPoistaTilitapahtumatDialogData, AsiakkaanMaksutavatPoistaTilitapahtumatDialogResult>(
      AsiakkaanMaksutavatPoistaTilitapahtumatDialog,
      { data: dialogData, width: '500px' },
    )

    const result = await firstValueFrom(dialogRef.afterClosed())
    if (!result) {
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const pyynto: PoistaTilitapahtumatJaKirjauksetPyynto = {
        asiakasAvain: this.asiakas.avain,
        iban,
        start: result.start,
        end: result.end,
      }
      const vastaus = await this._firebaseLemonator.functionsCall<PoistaTilitapahtumatJaKirjauksetPyynto, PoistaTilitapahtumatJaKirjauksetVastaus>('kirjanpitoPoistaTilitapahtumatJaKirjaukset', pyynto)

      if (vastaus?.e === 'ei-avoimella-tilikaudella') {
        this._snackbar.open('Valitulla aikavälillä ei löydy ei-lukittuja tilikausia, peruutetaan toiminto.', 'OK')
        return
      }

      if (!vastaus || vastaus?.e) {
        throw new Error(vastaus?.e || 'No response')
      }

      this._snackbar.open(`Poistettu ${vastaus.poistettujenTilitapahtumienMaara} tilitapahtumaa ja ${vastaus.poistettujenKirjaustenMaara} kirjausta.`, 'OK')
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Poistaminen epäonnistui: ' + (err?.message || 'Tuntematon virhe'), 'OK')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async cancelScheduledChange(maksutapaIndex: number) {
    const maksutapaJaKayttajat = this.maksutavatJaKayttajat[maksutapaIndex]
    const maksutapa = maksutapaJaKayttajat.maksutapa

    if (!maksutapa.scheduledProviderChange) {
      return
    }

    const areYouSureDialogData: AreYouSureDialogData = {
      header: 'Peruuta PSD2 palveluntarjoajan vaihto',
      text: 'Haluatko varmasti peruuttaa palveluntarjoajan vaihdon tälle maksutavalle?',
      rightAction: 'Kyllä',
      leftAction: 'Ei',
    }

    const isSure = await firstValueFrom(this._dialog.open<AreYouSureDialog, AreYouSureDialogData>(AreYouSureDialog, { data: areYouSureDialogData }).afterClosed())
    if (!isSure) {
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const firestoreProvider = this._firebaseLemonator.firestoreProvider
      const batch = this._firebaseLemonator.firestoreBatch()

      const vaihtoTyoAvain = maksutapa.scheduledProviderChange.ajastettuTyoId
      const yhteystarkistusTyoAvain = `${vaihtoTyoAvain}-yhteystarkistus`
      this._ajastettuTyoService.poistaAjastus(vaihtoTyoAvain, firestoreProvider, batch as any)
      this._ajastettuTyoService.poistaAjastus(yhteystarkistusTyoAvain, firestoreProvider, batch as any)

      const maksutapaUri = 'asiakkaat/' + this.asiakas.avain + '/maksutavat/' + maksutapa.avain
      const maksutapaUpdateData: Pick<AsiakkaanMaksutapa, 'scheduledProviderChange'> = {
        scheduledProviderChange: this._firebaseLemonator.firestoreDeleteMarker(),
      }
      batch.update(maksutapaUri, maksutapaUpdateData)

      await batch.commit()

      this._snackbar.open('Ajastettu palveluntarjoajan vaihto peruutettu.', 'OK', { duration: 5000 })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Virhe peruuttaessa ajastettua vaihtoa: ' + err.message, 'OK', { duration: 5000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }
}
